import { IonListHeader } from '@ionic/react';
import { useFormikContext } from 'formik';
import { DebouncedFunc, debounce, get } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect } from 'react-use';
import * as yup from 'yup';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { FlowItemEditCommonProps } from '../../../../shared/component/flow-item/edit/flow-item-edit.types';
import { ParametersList } from '../../../../shared/component/flow-item-parameter/parameter-list';
import { WidgetExtensions } from '../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItemForm,
  FlowItemParameterMetadata,
} from '../../../../shared/model/flow.types';
import { BeaconPaintingData } from '../../model/beacon-painting-data';
import { getPaintingParameterMetadataFromForm } from '../../util/beacon-painting.util';

export interface PaintProcessSettingsFormProps
  extends Omit<FlowItemEditCommonProps, 'item'> {
  extensions?: WidgetExtensions;
  readOnly?: boolean;
  parameterOptions?: BeaconPaintingData.GetParameterMetadataFromFormOptions;
  setValidationSchema: (schema: yup.AnySchema | undefined) => void;
  formikValuePath?: string;
  parameterListPrefix?: string;
  recalculateParameterValues?: (form: FlowItemForm) => FlowItemForm;
}

const PaintProcessSettingsFormHeader = (props: {
  header?: string;
}): ReactElement => {
  const { header } = props;

  return (
    <div className="configuration-header">
      <IonListHeader>
        {header ??
          getLocalizedText('common:application_settings_parameters_header')}
      </IonListHeader>
    </div>
  );
};

const PaintProcessSettingsFormWithHeader = (
  props: PaintProcessSettingsFormProps & { header?: string }
): ReactElement => {
  const { header, ...paintProcessSettingsProps } = props;

  return (
    <>
      <PaintProcessSettingsFormHeader header={header} />
      <PaintProcessSettingsForm {...paintProcessSettingsProps} />
    </>
  );
};

const PaintProcessSettingsForm = (
  props: PaintProcessSettingsFormProps
): ReactElement | null => {
  const {
    extensions,
    readOnly,
    system,
    setValidationSchema,
    formikValuePath,
    parameterListPrefix,
    parameterOptions,
    recalculateParameterValues,
  } = props;

  const { flow, flowItemService } = useFlowServices();
  const [parameters, setParameters] = useState<FlowItemParameterMetadata[]>([]);

  const formik = useFormikContext<FlowItemForm>();

  const [parameterFormOptions, setParameterFormOptions] =
    useState<BeaconPaintingData.GetParameterMetadataFromFormOptions>(
      parameterOptions ?? {}
    );

  useDeepCompareEffect(() => {
    setParameterFormOptions({
      ...parameterOptions,
    });
  }, [parameterOptions ?? {}]);

  const onChangeForm = useCallback(
    (
      form: FlowItemForm,
      parameterFormOptions?: BeaconPaintingData.GetParameterMetadataFromFormOptions
    ) => {
      let actualForm: FlowItemForm = formikValuePath
        ? (get(form, formikValuePath) as FlowItemForm)
        : form;

      if (recalculateParameterValues) {
        actualForm = recalculateParameterValues(actualForm);
      }

      const parameters = getPaintingParameterMetadataFromForm(
        flowItemService,
        actualForm.parameters,
        parameterFormOptions
      );

      setParameters(parameters);

      const schema = flowItemService.getYupSchemaForItem(
        BeaconPaintingData.FlowItemRef.PAINT,
        props.system,
        {
          flow,
          form: actualForm,
        },
        parameters
      );
      setValidationSchema(schema);
    },
    [
      formikValuePath,
      recalculateParameterValues,
      flow,
      flowItemService,
      props.system,
      setValidationSchema,
    ]
  );

  const debouncedOnChangeForm =
    useRef<DebouncedFunc<(form: FlowItemForm) => void>>();

  useEffect(() => {
    debouncedOnChangeForm.current = debounce((form: FlowItemForm) => {
      onChangeForm(form, parameterFormOptions);
    }, 300);

    return () => {
      debouncedOnChangeForm.current?.cancel();
    };
  }, [onChangeForm, parameterFormOptions]);

  useEffect(() => {
    onChangeForm(formik.values, parameterFormOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterFormOptions]);

  useEffect(() => {
    if (debouncedOnChangeForm.current) {
      debouncedOnChangeForm.current(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const showPaintSettings = useMemo(() => {
    return parameters.length > 0;
  }, [parameters.length]);

  const parameterExtensions = useMemo(() => {
    return {
      widgetExtensions: extensions,
    };
  }, [extensions]);

  return showPaintSettings ? (
    <ParametersList
      parameters={parameters}
      system={system}
      readOnly={readOnly}
      extensions={parameterExtensions}
      formPrefix={parameterListPrefix}
    />
  ) : null;
};

export {
  PaintProcessSettingsForm,
  PaintProcessSettingsFormHeader,
  PaintProcessSettingsFormWithHeader,
};
